import { useEffect, useRef, useState } from 'react';
import {
  deviceStatus,
  fetchSnapshotWithDevice,
  generateKeys,
  pairDevice,
} from '../offline/sync.js';
import type { PosStore } from '../offline/store.js';
import { API_BASE } from '../config.js';

interface Props {
  store: PosStore;
  onReady: () => void;
  onRegister: () => void;
  onAdmin: () => void;
}

const POLL_MS = 3000;

/**
 * Persediaan peranti — KOD PAIRING SAHAJA.
 *
 * Tiada e-mel, tiada kata laluan pemilik. Kasir yang mengambil PC baharu tidak sepatutnya memerlukan
 * kredensial pemilik, dan kata laluan itu memberi akses kepada laporan kewangan — pendedahan yang
 * tidak perlu hanya untuk memasang terminal.
 *
 * Kod pairing membuktikan pengurus membenarkan PC ini. Selepas itu peranti mempunyai kunci sendiri,
 * dan pengurus MESTI meluluskannya di back office sebelum peranti boleh menerima wang. Itulah
 * semakan manusia yang menggantikan kata laluan.
 */
export function SetupScreen({ store, onReady, onRegister, onAdmin }: Props): React.ReactElement {
  const [code, setCode] = useState('');
  const [agentToken, setAgentToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState('');

  /** Peranti sudah dipasangkan tetapi menunggu kelulusan pengurus. */
  const [waiting, setWaiting] = useState<{ deviceNo: number } | null>(null);
  const pollTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (pollTimer.current !== null) window.clearInterval(pollTimer.current);
    };
  }, []);

  async function finishSetup(): Promise<void> {
    const identity = await store.getIdentity();
    const keys = await store.getKeys();
    if (identity === undefined || keys === undefined) {
      setError('Identiti peranti hilang. Pasangkan semula.');
      return;
    }

    setStep('Memuat turun katalog…');
    const snapshot = await fetchSnapshotWithDevice(API_BASE, identity.deviceId, keys.privateKey);
    await store.applySnapshot(snapshot);
    setStep('Siap');
    onReady();
  }

  function startPolling(): void {
    if (pollTimer.current !== null) window.clearInterval(pollTimer.current);

    pollTimer.current = window.setInterval(() => {
      void (async () => {
        const identity = await store.getIdentity();
        const keys = await store.getKeys();
        if (identity === undefined || keys === undefined) return;

        const status = await deviceStatus(API_BASE, identity.deviceId, keys.privateKey);
        // Rangkaian gagal (`null`): jangan andaikan apa-apa — tunggu kitaran seterusnya.
        if (status === null || status.status === 'pending') return;

        if (pollTimer.current !== null) window.clearInterval(pollTimer.current);
        pollTimer.current = null;

        if (status.status === 'active') {
          setWaiting(null);
          try {
            await finishSetup();
          } catch (caught) {
            setError(caught instanceof Error ? caught.message : 'gagal memuat turun katalog');
          }
        } else {
          setError('Peranti ini telah dicabut. Minta pengurus menjana kod baharu.');
          setWaiting(null);
        }
      })();
    }, POLL_MS);
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      setStep('Menjana kunci peranti…');
      const keys = await generateKeys();

      setStep('Memasangkan peranti…');
      const paired = await pairDevice(API_BASE, {
        code,
        publicKey: keys.publicKey,
        keyProtection: keys.kind ?? 'exportable',
        agentVersion: 'web-0.1.0',
      });

      // Peranti ini mungkin dipasangkan semula oleh orang lain. Sesi kasir lama dan namanya
      // dibuang supaya jualan orang baharu tidak dikaitkan dengan nama orang lama.
      window.localStorage.removeItem('72on:cashier');
      await store.clearCashierToken();

      await store.saveKeys(keys);
      await store.saveIdentity(paired.identity);
      // Teruskan penomboran resit cawangan; peranti baharu tidak bermula di 0001.
      await store.seedClientSeq(paired.lastClientSeq);
      if (agentToken.trim() !== '') await store.saveAgentToken(agentToken.trim());

      if (paired.status === 'active') {
        await finishSetup();
        return;
      }

      setStep('');
      setWaiting({ deviceNo: paired.identity.deviceNo });
      startPolling();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'persediaan gagal');
      setStep('');
    } finally {
      setBusy(false);
    }
  }

  if (waiting !== null) {
    return (
      <div className="setup">
        <h1>Menunggu kelulusan</h1>
        <p className="muted">
          Peranti ini dipasangkan sebagai <strong>kaunter {waiting.deviceNo}</strong>, tetapi belum
          boleh menerima jualan sehingga pengurus meluluskannya.
        </p>
        <p className="muted">
          Pengurus: buka <strong>app.72on.com/admin</strong> → Peranti → tekan{' '}
          <strong>Luluskan</strong>.
        </p>
        <p className="muted">Halaman ini akan meneruskan sendiri sebaik diluluskan.</p>
        {error !== null && <p className="error">{error}</p>}
        <div className="actions">
          <button type="button" className="ghost" onClick={onAdmin}>
            Buka back office
          </button>
          <button type="button" onClick={() => void finishSetup().catch(() => undefined)}>
            Semak semula
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="setup">
      <h1>Sediakan peranti</h1>
      <p className="muted">
        Minta pengurus menjana kod pairing di <strong>app.72on.com/admin</strong> → Peranti. Kod sah
        15 minit dan hanya boleh digunakan sekali. Tiada kata laluan pemilik diperlukan di PC ini.
      </p>

      <form onSubmit={submit}>
        <label>
          Kod pairing (8 aksara)
          <input
            value={code}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
            maxLength={8}
            required
            autoFocus
            autoCapitalize="characters"
            spellCheck={false}
            placeholder="ABCD2345"
          />
        </label>

        <fieldset>
          <legend>Ejen cetak (pilihan)</legend>
          <label>
            Token ejen
            <input
              value={agentToken}
              onChange={(event) => setAgentToken(event.target.value)}
              placeholder="Dipaparkan di tetingkap ejen pada larian pertama"
              spellCheck={false}
            />
          </label>
          <p className="muted">
            Kosongkan jika printer belum dipasang. Boleh diisi kemudian di back office → Ejen cetak.
          </p>
        </fieldset>

        {error !== null && <p className="error">{error}</p>}
        {step !== '' && <p className="muted">{step}</p>}

        <div className="actions">
          <button type="button" className="ghost" onClick={onAdmin} disabled={busy}>
            Log masuk back office
          </button>
          <button type="button" className="ghost" onClick={onRegister} disabled={busy}>
            Kedai baharu? Daftar
          </button>
          <button type="submit" disabled={busy}>
            {busy ? 'Memproses…' : 'Sediakan peranti'}
          </button>
        </div>
      </form>
    </div>
  );
}
